if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          pagination: this.querySelector('[data-media-pagination]'),
          dots: this.querySelectorAll('[data-media-dot]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails && !this.elements.dots.length) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.elements.dots.length) {
          this.elements.dots.forEach((dot) => {
            dot.addEventListener('click', this.setActiveMedia.bind(this, dot.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.initThumbnailLeftHeight();
        this.initThumbnailLeftArrows();
      }

      initThumbnailLeftHeight() {
        const thumbSlider = this.querySelector('.thumbnail-slider');
        const viewer = this.querySelector('[id^="GalleryViewer"]');
        if (!thumbSlider || !viewer || this.dataset.desktopLayout !== 'thumbnail_left') return;

        const sync = () => {
          const activeItem = viewer.querySelector('.product__media-item.is-active') || viewer.querySelector('.product__media-item');
          if (!activeItem) return;
          thumbSlider.style.maxHeight = activeItem.offsetHeight + 'px';
        };

        sync();
        new ResizeObserver(sync).observe(viewer);
      }

      initThumbnailLeftArrows() {
        const upBtn = this.querySelector('.thumbnail-left__arrow--up');
        const downBtn = this.querySelector('.thumbnail-left__arrow--down');
        if (!upBtn || !downBtn) return;
        const list = this.querySelector('.thumbnail-list.slider--vertical');
        if (!list) return;
        const scrollAmount = 350;

        const updateArrowStates = () => {
          const atTop = list.scrollTop <= 1;
          const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
          upBtn.style.opacity = atTop ? '0.5' : '1';
          downBtn.style.opacity = atBottom ? '0.5' : '1';
        };

        updateArrowStates();
        list.addEventListener('scroll', updateArrowStates);

        upBtn.addEventListener('click', () => list.scrollBy({ top: -scrollAmount, behavior: 'smooth' }));
        downBtn.addEventListener('click', () => list.scrollBy({ top: scrollAmount, behavior: 'smooth' }));
      }

      onSlideChanged(event) {
        this.setActiveDot(event.detail.currentElement.dataset.mediaId);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails || this.elements.dots.length) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.setActiveDot(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots.length) return;

        this.elements.dots.forEach((dot) => {
          const isActive = dot.dataset.target === mediaId;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
